import moment from 'moment-timezone';

import {
	DEADHEAD_ACTIVITY_TYPE,
	DRIVING_ACTIVITY_TYPE,
	HOSTING_ACTIVITY_TYPE,
	ON_ASSIGNMENT_ACTIVITY_TYPE,
	ON_ASSIGNMENT_STATUS_TYPE,
	ON_ASSIGNMENT_UNKNOWN_ACTIVITY_TYPE,
	PROTECTION_ACTIVITY_TYPE,
	TRAVELLING_ACTIVITY_TYPE,
	WORK_ASSIGNMENT_ACTIVITY_TYPE,
} from 'constants/activityTypes';
import { mapDriverActivities, SHIFT_GROUP_TYPENAME } from 'utils/crewTimelineActivities';
import { getDaysToUTCRangeMap } from 'utils/datetime';

import { DAY_CREW_TIMELINE_FORMAT } from './const';

export { mapDriverActivities, SHIFT_GROUP_TYPENAME };

/**
 * Maps a work-assignment activity type to the coarse status key shown in the UI.
 * Driving, hosting, deadhead, and travelling all roll up to `onassignment`;
 * everything else falls back to `unknown`.
 *
 * @param {string} activityType - Raw activity type string from the API.
 * @returns {string} Coarse status key.
 */
const getStatusKey = (activityType) => {
	switch (activityType) {
		case DRIVING_ACTIVITY_TYPE:
		case HOSTING_ACTIVITY_TYPE:
		case DEADHEAD_ACTIVITY_TYPE:
		case TRAVELLING_ACTIVITY_TYPE:
			return ON_ASSIGNMENT_ACTIVITY_TYPE;
		default:
			return ON_ASSIGNMENT_UNKNOWN_ACTIVITY_TYPE;
	}
};

/**
 * Derives a structured driver status from the API's availability payload.
 *
 * @param {Object|null} availabilityInfo - Driver availability from the API, or null if unavailable.
 * @returns {{ status: string, isWAStatus: boolean, activityType: string }} Normalized status object.
 */
const getDriverStatus = (availabilityInfo) => {
	if (!availabilityInfo) {
		return {
			status: ON_ASSIGNMENT_UNKNOWN_ACTIVITY_TYPE,
			isWAStatus: false,
			activityType: ON_ASSIGNMENT_UNKNOWN_ACTIVITY_TYPE,
		};
	}

	const { currentActivity, status } = availabilityInfo;
	const normalizedStatus = status?.toLowerCase() ?? ON_ASSIGNMENT_UNKNOWN_ACTIVITY_TYPE;

	if (
		status === ON_ASSIGNMENT_STATUS_TYPE &&
		currentActivity?.activity &&
		currentActivity.activity.__typename === WORK_ASSIGNMENT_ACTIVITY_TYPE
	) {
		const waStatus = currentActivity.activity.activityType;
		return {
			status: waStatus.toLowerCase(),
			isWAStatus: true,
			activityType: getStatusKey(waStatus),
		};
	}

	return {
		status: normalizedStatus,
		isWAStatus: false,
		activityType: normalizedStatus,
	};
};

/**
 * Derives a driver's current or next location from availability and activity data.
 *
 * Priority order:
 * 1. `currentActivity` on `availabilityInfo` (work-assignment context).
 * 2. Protection activity from `activitiesV2`.
 * 3. `currentLocation` fallback.
 *
 * @param {Object|null} availabilityInfo - Driver availability payload from the API.
 * @param {Object|null} currentLocation - Fallback location object (`{ station?, city? }`).
 * @param {Array<Object>} activitiesV2 - Full activity list used for protection-activity lookup.
 * @returns {{ location: string, locationLabel: string }} Human-readable location and its label.
 */
export const getDriverLocation = (availabilityInfo, currentLocation, activitiesV2) => {
	let location = 'Unknown';
	let locationLabel = 'Location';

	if (availabilityInfo?.currentActivity) {
		if (
			availabilityInfo.currentActivity.activity &&
			availabilityInfo.currentActivity.activity.__typename === WORK_ASSIGNMENT_ACTIVITY_TYPE
		) {
			const { toStation, activityType } = availabilityInfo.currentActivity.activity;
			if (toStation && toStation.city) {
				location = `${toStation.code} (${toStation.city.name})`;
				locationLabel =
					activityType === PROTECTION_ACTIVITY_TYPE ? 'Current location' : 'Next location';
				return { location, locationLabel };
			}
		}
	}

	const protectionActivity = activitiesV2?.find(
		(item) =>
			item.activity?.__typename === WORK_ASSIGNMENT_ACTIVITY_TYPE &&
			item.activity.activityType === PROTECTION_ACTIVITY_TYPE
	);

	if (protectionActivity) {
		const { toStation } = protectionActivity.activity;
		if (toStation && toStation.city) {
			location = `${toStation.code} (${toStation.city.name})`;
			locationLabel = 'Current location';
			return { location, locationLabel };
		}
	}

	if (currentLocation) {
		locationLabel = 'Current location';
	}

	if (currentLocation?.station && currentLocation?.city) {
		location = `${currentLocation.station.code} (${currentLocation.city.name})`;
	} else if (currentLocation?.city) {
		location = currentLocation.city.name;
	}

	return { location, locationLabel };
};

const createDateRange = (startTimestamp, endTimeStamp, timezone) => {
	const rangeMap = new Map(
		getDaysToUTCRangeMap(startTimestamp, endTimeStamp, timezone, DAY_CREW_TIMELINE_FORMAT)
	);
	return [...rangeMap.values()];
};

export const mapCrewTimelineTable = (
	drivers,
	startTimestamp,
	endTimeStamp,
	timezone = moment.tz.guess()
) => {
	const dateRange = createDateRange(startTimestamp, endTimeStamp, timezone);

	const mappedDrivers = drivers.map((driver) => {
		const { activitiesV2, fullName, driverCard, id, crewMember, shifts } = driver;
		const { availabilityInfo, currentRun, currentLocation, currentHomeBase } = crewMember || {};
		const idleTimeMinutes = availabilityInfo?.idleTimeMinutes ?? null;

		const driverStatus = getDriverStatus(availabilityInfo);
		const { location, locationLabel } = getDriverLocation(
			availabilityInfo,
			currentLocation,
			activitiesV2
		);
		const activities = mapDriverActivities(activitiesV2 || [], shifts || [], id, {
			groupByShift: true,
		});

		return {
			id,
			fullName,
			idleTimeMinutes,
			driverCard,
			driverStatus,
			location,
			locationLabel,
			currentHomeBase,
			currentRun,
			activities,
			shifts,
		};
	});

	return {
		dateRange,
		drivers: mappedDrivers,
		timezone,
	};
};
